import AppLayout, { AppLayoutProps } from "@amzn/awsui-components-react/polaris/app-layout";
import CustomGridLayout from "../../common/component/CustomGridLayout/CustomGridLayout";
import NavigationBreadcrumbs from "../../common/navigation/NavigationBreadcrumbs";
import Paths from "common/paths/paths";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import FilterSection from "../../common/component/FilterSection/FilterSection";
import { CatalogItem } from "interfaces";
import LocalITHardwareRestockContent from "../../common/component/LocalITHardwareRestockContent/LocalITHardwareRestockContent";
import { useNewOrderNavigate } from "hooks/useNewOrder";
import useCatalog, {
    SEARCH_HARDWARE_PROPERTY_KEYS,
    URL_FILTER_KEYS,
    useCategories,
    useFiltering,
    useFiltersInUrl,
} from "hooks/useCatalog";
import { JSX, useCallback, useEffect, useState } from "react";
import { GridProps, PropertyFilterProps } from "@amzn/awsui-components-react";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import CheckboxFilter from "../../common/component/FilterSection/CheckboxFilter/CheckboxFilter";
import useFlashbar from "hooks/useFlashbar";
import CustomPropertyFilter from "common/component/CustomPropertyFilter/CustomPropertyFilter";
import { HARDWARE_LOCATION_SELECT, HARDWARE_RESTOCK_SEARCH_BAR } from "helpers";
import { useLocation, useSearchParams } from "react-router-dom";
import { compressFilters } from "common/utils/compress-url";
import CustomSelectFormField from "common/component/CustomFormField/CustomSelectFormField";
import { useCountries } from "hooks/useBuildings";
import { UserPermissions } from "services/user-permissions";
import { PERMISSIONS } from "configuration/config";

const getStringPath: string = "views.itHardwareRestock";
const getStringPathFilter: string = `${getStringPath}.filter`;
const getStringPathNotification = `${getStringPath}.infiniteScrollingNotification`;
const getStringSearchHardware = `${getStringPath}.searchHardware`;
const getStringFilterByCountry = `${getStringPath}.filterByCountry`;

/**
 * Defines the maximum amount of characters until the url is compressed.
 * This prevents extremely long URLs that can cause access issues.
 */
export const MAXIMUM_CHARACTERS_UNTIL_COMPRESSED = 500;

/**
 * Builds URL parameters from categories and filtering query for hardware restock search.
 * Compresses the URL when it exceeds the maximum character limit to prevent access issues.
 *
 * @param categories - Array of selected category strings
 * @param filteringQuery - Property filter query containing tokens and filters
 * @returns URLSearchParams object with encoded filters, compressed if necessary
 */
export const buildUrlParams = (
    categories: string[],
    filteringQuery: PropertyFilterProps.Query,
    country: string | undefined,
): URLSearchParams => {
    const urlParams = new URLSearchParams();

    // Add categories
    if (categories?.length) {
        urlParams.set(URL_FILTER_KEYS.CATEGORIES, categories.join(","));
    }

    // Parse query tokens
    const keywords: string[] = [];
    const propertyFilters = new Map<string, string[]>();
    const validKeys = Object.values(SEARCH_HARDWARE_PROPERTY_KEYS);

    // Initialize property filters
    validKeys.forEach((key) => propertyFilters.set(key, []));

    // Process tokens
    filteringQuery.tokens.forEach((token) => {
        if (!token.propertyKey) {
            keywords.push(token.value);
        } else if (validKeys.includes(token.propertyKey)) {
            const values = propertyFilters.get(token.propertyKey)!;
            values.push(token.value);
        }
    });

    // Add to URL params
    if (keywords.length) {
        urlParams.set(URL_FILTER_KEYS.KEYWORDS, keywords.join(","));
    }

    propertyFilters.forEach((values, key) => {
        if (values.length) {
            urlParams.set(key, values.join(","));
        }
    });

    // Add country to params
    if (UserPermissions.instance.hasPermission(PERMISSIONS.ITSE_FULL_CATALOG_ACCESS) && country) {
        urlParams.set(URL_FILTER_KEYS.COUNTRY, country);
    }

    // Compress URL when it exceeds maximum length to prevent access issues
    const paramsString = urlParams.toString();
    if (paramsString.length > MAXIMUM_CHARACTERS_UNTIL_COMPRESSED) {
        const compressed = new URLSearchParams();
        compressed.set("f", compressFilters(urlParams));
        return compressed;
    }

    return urlParams;
};

/**
 * Renders the LocalITHardwareRestock component with filtering and content sections.
 * Manages URL synchronization for filters and categories, handles infinite scrolling,
 * and provides navigation to new order creation.
 *
 * @returns AppLayout containing filter section and hardware restock content
 */
const LocalITHardwareRestock = (): JSX.Element => {
    // Generate infinite scrolling notification message
    const { Flashbar, generateMessage } = useFlashbar(true);

    useEffect(() => {
        generateMessage({
            type: "info",
            header: getString(`${getStringPathNotification}.title`),
            content: getString(`${getStringPathNotification}.content`),
            dismissible: true,
        });
    }, []);

    // Configure app layout content type
    const contentType: AppLayoutProps.ContentType = "table";

    // Define responsive grid layout for filter and content sections
    const gridDefinition: ReadonlyArray<GridProps.ElementDefinition> = [
        { colspan: { default: 3, xl: 2 } },
        { colspan: { default: 9, xl: 10 } },
    ];

    // Configure navigation breadcrumbs
    const breadcrumbs = {
        items: [
            { text: Paths.itOrderList.name, href: Paths.itOrderList.url },
            { text: Paths.itHardwareRestock.name, href: Paths.itHardwareRestock.url },
        ],
    };

    // Manage URL filter synchronization and state
    const [searchParams, setSearchParams] = useSearchParams();
    const urlFilters = useFiltersInUrl();
    const location = useLocation();
    const [selectedCategories, setSelectedCategories] = useState<string[]>(urlFilters.parseCategories());
    const [selectedQuery, setSelectedQuery] = useState(urlFilters.parseQuery());
    const [isUrlSyncing, setIsUrlSyncing] = useState(true);

    const [selectedCountry, setSelectedCountry] = useState<OptionDefinition>(urlFilters.parseCountry());
    const { options } = useCountries();

    // Initialize catalog data and filtering hooks
    const categories = useCategories({}, selectedCategories);
    const filtering = useFiltering(selectedQuery);
    const catalog = useCatalog({
        categories: categories.selectedFilters,
        hardwareFilters: filtering.filters,
        keywords: filtering.keywords,
        isLoadingCat: categories.updatedCategoriesLoading,
        location: { country: selectedCountry?.value },
    });

    // Synchronize URL parameter changes to local component state
    useEffect(() => {
        if (isUrlSyncing || categories.isLoadingCat) return;

        const urlCategories = urlFilters.parseCategories();
        const urlQuery = urlFilters.parseQuery();
        const urlCountry = urlFilters.parseCountry();

        if (JSON.stringify(urlCategories) !== JSON.stringify(selectedCategories)) {
            setSelectedCategories(urlCategories);
        }

        if (JSON.stringify(urlQuery) !== JSON.stringify(selectedQuery)) {
            setSelectedQuery(urlQuery);
        }

        if (
            UserPermissions.instance.hasPermission(PERMISSIONS.ITSE_FULL_CATALOG_ACCESS) &&
            urlCountry.value !== selectedCountry.value
        ) {
            setSelectedCountry(urlCountry);
        }
    }, [location.search, categories.isLoadingCat, isUrlSyncing]);

    /**
     * Synchronizes filter changes to URL parameters.
     * Prevents infinite loops by managing sync state and checking for actual changes.
     */
    const syncFiltersToUrl = useCallback(() => {
        if (categories.isLoadingCat) return;

        setIsUrlSyncing(true);
        const newUrlParams = buildUrlParams(categories.selectedFilters || [], filtering.query, selectedCountry.value);

        if (searchParams.toString() !== newUrlParams.toString()) {
            setSearchParams(newUrlParams);
        }

        setIsUrlSyncing(false);
    }, [categories.selectedFilters, filtering.query, categories.isLoadingCat, selectedCountry]);

    useEffect(() => {
        syncFiltersToUrl();
    }, [syncFiltersToUrl]);

    // Configure new order navigation with selected items

    const { navigateToNewOrder } = useNewOrderNavigate({
        categories: categories.categories,
    });

    const onSubmitNewOrder = (params: { quantities: Map<CatalogItem, number> }) => {
        const isOrderStandard: boolean = true;
        navigateToNewOrder({ itemsSelected: params.quantities, isOrderStandard });
    };

    return (
        <AppLayout
            navigationHide={true}
            toolsHide={true}
            breadcrumbs={<NavigationBreadcrumbs items={breadcrumbs.items} />}
            notifications={Flashbar}
            content={
                <CustomGridLayout
                    title={getString(`${getStringPath}.title`)}
                    leftSection={
                        <FilterSection>
                            {UserPermissions.instance.hasPermission(PERMISSIONS.ITSE_FULL_CATALOG_ACCESS) && (
                                <CustomSelectFormField
                                    label={getString(`${getStringFilterByCountry}.label`)}
                                    placeholder={getString(`${getStringFilterByCountry}.placeholder`)}
                                    value={selectedCountry}
                                    setValue={setSelectedCountry}
                                    testId={HARDWARE_LOCATION_SELECT}
                                    options={options}
                                    touchpoint="intake-filter-catalog-by-country"
                                />
                            )}
                            <CustomPropertyFilter
                                label={getString(`${getStringSearchHardware}.label`)}
                                placeholder={getString(`${getStringSearchHardware}.placeholder`)}
                                onFilterChange={({ detail }) => filtering.setQuery(detail)}
                                query={filtering.query}
                                testId={HARDWARE_RESTOCK_SEARCH_BAR}
                                filteringProperties={filtering.filteringProperties}
                                readOnlyOperations={true}
                                touchpoint="intake-search-hardware"
                            />
                            <CheckboxFilter
                                name={getString(`${getStringPathFilter}.catalog`)}
                                isLoading={categories.isLoadingCat}
                                checkBoxStructure={categories.filters}
                                setCheckBoxStructure={categories.setFilters}
                                touchpoint="intake-hardware-categories"
                            />
                        </FilterSection>
                    }
                    rightSection={
                        <LocalITHardwareRestockContent
                            catalog={catalog.data}
                            trackBy={catalog.trackBy}
                            error={catalog.error}
                            isLoading={catalog.isLoading}
                            onSubmitNewOrder={onSubmitNewOrder}
                            hasMore={catalog.data.length > 0 && catalog.hasMore}
                            onLoadMore={catalog.loadMore}
                            isLoadingMore={catalog.isLoadingMore}
                        />
                    }
                    gridDefinition={gridDefinition}
                />
            }
            contentType={contentType}
        />
    );
};

export default LocalITHardwareRestock;
